import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, finalize, forkJoin, Subject, Subscription } from 'rxjs';
import { RawMaterialService } from '../../raw-material.service';
import {
  RawMaterialCategoryItem,
  RawMaterialItem,
  RawMaterialWarehouse,
  StockAdjustment,
  StockAdjustmentContext,
  StockAdjustmentDirection,
  StockAdjustmentFilters,
  StockAdjustmentReason,
  STOCK_ADJUSTMENT_REASON_LABELS,
  STOCK_ADJUSTMENT_REASON_OPTIONS,
} from '../../raw-material.model';
import { EmployeesService } from '../../../employees/employees.service';
import { EmployeeBasicInfo } from '../../../employees/employees.model';
import { PaginatedResponse } from '../../../common/models/common.model';
import { absDecimalString, decimalSign, formatDecimalQuantity, formatSignedDecimalQuantity } from '../../../utils';
import { StockAdjustmentPrefill } from '../stock-adjustment-form/stock-adjustment-form.component';

const PAGE_SIZE = 10;

@Component({
  selector: 'app-stock-adjustments',
  standalone: false,
  templateUrl: './stock-adjustments.component.html',
  styleUrl: './stock-adjustments.component.scss',
})
export class StockAdjustmentsComponent implements OnInit, OnDestroy {
  @Input() wareHouses: RawMaterialWarehouse[] = [];

  @ViewChild('adjustmentModal') adjustmentModalRef!: TemplateRef<any>;

  protected adjustments: StockAdjustment[] = [];
  protected categories: RawMaterialCategoryItem[] = [];
  protected rawMaterials: RawMaterialItem[] = [];
  protected employees: EmployeeBasicInfo[] = [];

  protected isLoading: boolean = false;
  protected isForbidden: boolean = false;
  protected pageError: any = null;

  protected currentPage: number = 1;
  protected totalCount: number = 0;
  protected hasNextPage: boolean = false;
  protected hasPreviousPage: boolean = false;

  protected searchTerm: string = '';
  protected selectedRawMaterial: string | null = null;
  protected selectedWarehouse: string | null = null;
  protected selectedCategory: string | null = null;
  protected selectedReason: StockAdjustmentReason | null = null;
  protected selectedCreatedBy: string | null = null;
  protected selectedDirection: StockAdjustmentDirection | null = null;
  protected fromDate: string | null = null;
  protected toDate: string | null = null;

  /** Context and starting values handed to the form when reversing an adjustment. */
  protected modalContext: StockAdjustmentContext | null = null;
  protected modalPrefill: StockAdjustmentPrefill | null = null;

  protected readonly pageSize = PAGE_SIZE;
  protected readonly reasonOptions = STOCK_ADJUSTMENT_REASON_OPTIONS;
  protected readonly directionOptions = [
    { value: StockAdjustmentDirection.ADD, label: 'Added stock' },
    { value: StockAdjustmentDirection.REMOVE, label: 'Removed stock' },
  ];

  private modalRef: NgbModalRef | null = null;
  private listSubscription!: Subscription;
  private optionsSubscription!: Subscription;
  private readonly searchTerms: Subject<string> = new Subject<string>();

  constructor(
    private rawMaterialService: RawMaterialService,
    private employeesService: EmployeesService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.loadFilterOptions();
    this.fetchAdjustments();

    this.searchTerms.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => {
      this.currentPage = 1;
      this.fetchAdjustments();
    });
  }

  protected get hasActiveFilters(): boolean {
    return !!(
      this.searchTerm.trim() ||
      this.selectedRawMaterial ||
      this.selectedWarehouse ||
      this.selectedCategory ||
      this.selectedReason ||
      this.selectedCreatedBy ||
      this.selectedDirection ||
      this.fromDate ||
      this.toDate
    );
  }

  protected onSearchChange(term: string): void {
    this.searchTerm = term;
    this.searchTerms.next(term.trim());
  }

  protected onFilterChange(): void {
    this.currentPage = 1;
    this.fetchAdjustments();
  }

  protected clearFilters(): void {
    this.searchTerm = '';
    this.selectedRawMaterial = null;
    this.selectedWarehouse = null;
    this.selectedCategory = null;
    this.selectedReason = null;
    this.selectedCreatedBy = null;
    this.selectedDirection = null;
    this.fromDate = null;
    this.toDate = null;

    this.onFilterChange();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.fetchAdjustments();
  }

  /**
   * Adjustments are append-only: nothing is edited or deleted. An adjustment made in
   * error is undone by posting its opposite, which leaves both entries in the trail.
   */
  protected openReverseModal(adjustment: StockAdjustment): void {
    this.modalContext = {
      raw_material: adjustment.raw_material,
      raw_material_name: adjustment.raw_material_name,
      raw_material_code: adjustment.raw_material_code,
      warehouse: adjustment.warehouse,
      warehouse_name: adjustment.warehouse_name,
      unit_short_name: adjustment.unit_short_name,
      // The balance on the record is the one from that moment, not today's.
      units_available: null,
    };

    this.modalPrefill = {
      direction: decimalSign(adjustment.quantity) < 0 ? StockAdjustmentDirection.ADD : StockAdjustmentDirection.REMOVE,
      quantity: absDecimalString(adjustment.quantity),
      reason: StockAdjustmentReason.CORRECTION,
      remarks: this.buildReversalRemarks(adjustment),
    };

    this.modalRef = this.modalService.open(this.adjustmentModalRef, { size: 'lg', backdrop: 'static' });
  }

  protected onAdjustmentSaved(): void {
    this.closeModal();
    this.currentPage = 1;
    this.fetchAdjustments();
  }

  protected closeModal(): void {
    this.modalRef?.dismiss();
    this.modalRef = null;
    this.modalContext = null;
    this.modalPrefill = null;
  }

  protected isRemoval(adjustment: StockAdjustment): boolean {
    return decimalSign(adjustment.quantity) < 0;
  }

  protected signedQuantity(adjustment: StockAdjustment): string {
    return `${formatSignedDecimalQuantity(adjustment.quantity)} ${adjustment.unit_short_name}`;
  }

  protected balanceAfter(adjustment: StockAdjustment): string {
    return `${formatDecimalQuantity(adjustment.units_available_after)} ${adjustment.unit_short_name}`;
  }

  protected reasonLabel(adjustment: StockAdjustment): string {
    // Prefer our own wording; fall back to the server's if it ever sends a new code.
    return STOCK_ADJUSTMENT_REASON_LABELS[adjustment.reason] ?? adjustment.reason_display;
  }

  private buildReversalRemarks(adjustment: StockAdjustment): string {
    const date = new Date(adjustment.created_at);
    const when = Number.isNaN(date.getTime())
      ? adjustment.created_at
      : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

    return `Reverses the ${formatSignedDecimalQuantity(adjustment.quantity)} ${adjustment.unit_short_name} adjustment made on ${when}.`;
  }

  private fetchAdjustments(): void {
    this.pageError = null;
    this.isLoading = true;

    this.listSubscription?.unsubscribe();
    this.listSubscription = this.rawMaterialService
      .get_stock_adjustments(this.currentFilters)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: PaginatedResponse<StockAdjustment>) => {
          this.isForbidden = false;
          this.adjustments = response.results ?? [];
          this.totalCount = response.count ?? 0;
          this.hasNextPage = !!response.next;
          this.hasPreviousPage = !!response.previous;
        },
        error: (error: HttpErrorResponse) => {
          this.adjustments = [];
          this.totalCount = 0;

          // A 403 here is the endpoint's admin-only gate, not a fault to retry.
          if (error?.status === 403) {
            this.isForbidden = true;
            return;
          }

          this.pageError = error;
        },
      });
  }

  /** Every relation filter sends a UUID: a name comes back as an empty 200. */
  private get currentFilters(): StockAdjustmentFilters {
    return {
      page: this.currentPage,
      search: this.searchTerm.trim() || null,
      raw_material: this.selectedRawMaterial,
      warehouse: this.selectedWarehouse,
      category: this.selectedCategory,
      reason: this.selectedReason,
      created_by: this.selectedCreatedBy,
      direction: this.selectedDirection,
      from_date: this.fromDate,
      to_date: this.toDate,
    };
  }

  private loadFilterOptions(): void {
    this.optionsSubscription = forkJoin({
      categories: this.rawMaterialService.get_categories(),
      materials: this.rawMaterialService.list(),
      employees: this.employeesService.lighter_list(),
    }).subscribe({
      next: ({ categories, materials, employees }) => {
        this.categories = categories ?? [];
        this.rawMaterials = materials ?? [];
        this.employees = employees ?? [];
      },
      // Filter options are a convenience; the list itself still works without them.
      error: () => undefined,
    });
  }

  ngOnDestroy(): void {
    this.listSubscription?.unsubscribe();
    this.optionsSubscription?.unsubscribe();
    this.searchTerms.complete();
    this.modalRef?.dismiss();
  }
}
